"use client"

import { useEffect, useState } from "react"
import { LazyMessages } from "@/i18n/lazy-messages"
import {
  BiomarkersAiDrawer as Drawer,
  type BiomarkersAiDrawerProps,
} from "./biomarkers-ai-drawer-panel"

/**
 * The Ella side drawer, mounted the first time it opens.
 *
 * The drawer (AI answers with pricing and marker cards, plus the team chat
 * panel) reads tens of kB of messages and sits on every page through the
 * search modal's "Ask AI" mode, where most visitors never open it. Its
 * messages are the `ai-drawer` lazy bundle: fetched on first open, never in a
 * page's HTML. The drawer itself is `./biomarkers-ai-drawer-panel`; only this
 * module imports it.
 */
export function BiomarkersAiDrawer(props: BiomarkersAiDrawerProps) {
  const { open, askNonce, onClose } = props
  const [wanted, setWanted] = useState(false)
  // `askNonce` too: every "Ask AI" click bumps it, also one made while the
  // owner still thinks the drawer is open.
  useEffect(() => {
    if (open) setWanted(true)
  }, [open, askNonce])

  // The messages failed to load, so there is nothing to show. Close, so the
  // owner's state matches what is on screen, and the next click loads again.
  const giveUp = () => {
    setWanted(false)
    onClose()
  }

  return (
    <LazyMessages bundle="ai-drawer" load={wanted} onError={giveUp}>
      <Entering {...props} />
    </LazyMessages>
  )
}

/**
 * Mounts the drawer closed and opens it on the next frame, so the first open
 * slides in like every later one instead of appearing already open.
 */
function Entering(props: BiomarkersAiDrawerProps) {
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [])
  return <Drawer {...props} open={props.open && entered} />
}
